/**
 * FP3-033 — Student Progress Summary (Print).
 *
 * Meeting-friendly snapshot of one student. Reads goals + recent
 * sessions + active documents (last 5) + team via the existing
 * authenticated endpoints. Admin-only docs and private DMs are
 * filtered server-side already.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import {
  PrintShell,
  PrintAccessGate,
  classifyError,
} from "@/components/print/PrintShell";

function fmtPct(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${n.toFixed(0)}%`;
}

function summarizeMinutes(sessions) {
  // Iter — sum delivered minutes for the recent slice we have.
  let total = 0;
  let count = 0;
  for (const s of sessions || []) {
    const m = Number(s.minutes_actual ?? s.minutes_scheduled ?? 0);
    if (!Number.isNaN(m) && m > 0) {
      total += m;
      count += 1;
    }
  }
  return { total, count };
}

export default function PrintStudentProgressSummary() {
  const { studentId } = useParams();
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [student, setStudent] = useState(null);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.getStudent(studentId),
      api.listStudentDocuments(studentId).catch(() => ({ data: { items: [] } })),
    ])
      .then(([s, d]) => {
        if (cancelled) return;
        setStudent(s.data);
        setDocs((d.data?.items || []).filter((x) => x.status !== "archived").slice(0, 5));
        setPhase("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message);
        setPhase(classifyError(err));
      });
    return () => { cancelled = true; };
  }, [studentId]);

  const name = student ? `${student.first_name} ${student.last_name}` : "Student";
  const provider = student?.provider_details?.[0];
  const activeGoals = (student?.goals || []).filter(
    (g) => (g.status || "active") === "active"
  );
  const sessions = student?.sessions || [];
  const minutes = summarizeMinutes(sessions);

  return (
    <PrintShell
      title="Student Progress Summary"
      subtitle={`${name}${student?.readable_id ? ` · ${student.readable_id}` : ""}`}
      testId="print-progress-summary"
    >
      <PrintAccessGate phase={phase} error={error}>
        {student && (
          <>
            <div className="fp-print-section">
              <div className="fp-print-grid-2">
                <div>
                  <div className="fp-print-section-label">Student</div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{name}</div>
                  <div className="fp-print-kv-row">
                    <span className="fp-print-kv-key">Grade · School</span>
                    <span className="fp-print-kv-val">
                      {[student.grade && `Grade ${student.grade}`, student.school_name]
                        .filter(Boolean).join(" · ") || "—"}
                    </span>
                  </div>
                  <div className="fp-print-kv-row">
                    <span className="fp-print-kv-key">Service Areas</span>
                    <span className="fp-print-kv-val">
                      {(student.service_areas || []).join(", ") || "—"}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="fp-print-section-label">Provider</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {provider
                      ? `${provider.first_name || ""} ${provider.last_name || ""}`.trim()
                      : "—"}
                  </div>
                  <div className="fp-print-kv-row">
                    <span className="fp-print-kv-key">Service Type</span>
                    <span className="fp-print-kv-val">{student.service_type || "—"}</span>
                  </div>
                  <div className="fp-print-kv-row">
                    <span className="fp-print-kv-key">Sessions on file</span>
                    <span className="fp-print-kv-val">{sessions.length}</span>
                  </div>
                  <div className="fp-print-kv-row">
                    <span className="fp-print-kv-key">Recent delivered minutes</span>
                    <span className="fp-print-kv-val">{minutes.total} min ({minutes.count} sess.)</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="fp-print-section">
              <div className="fp-print-section-title">
                Active Goals ({activeGoals.length})
              </div>
              {activeGoals.length === 0 ? (
                <div className="fp-print-empty" style={{ padding: "12px 16px" }}>
                  No active goals.
                </div>
              ) : (
                <table className="fp-print-table" data-testid="progress-goals-table">
                  <thead>
                    <tr>
                      <th style={{ width: "55%" }}>Goal</th>
                      <th>Area</th>
                      <th>Baseline</th>
                      <th>Target</th>
                      <th>Current</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeGoals.map((g) => (
                      <tr key={g.id} data-testid={`progress-goal-row-${g.id}`}>
                        <td>{g.goal_text || "—"}</td>
                        <td>{g.goal_area || "—"}</td>
                        <td>{fmtPct(g.baseline_mastery)}</td>
                        <td>{fmtPct(g.target_mastery)}</td>
                        <td>{fmtPct(g.current_mastery)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="fp-print-section" data-testid="progress-recent-sessions">
              <div className="fp-print-section-title">Recent Sessions</div>
              {sessions.length === 0 ? (
                <div className="fp-print-empty" style={{ padding: "12px 16px" }}>
                  No sessions on file.
                </div>
              ) : (
                <table className="fp-print-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Minutes</th>
                      <th>Attendance</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.slice(0, 8).map((s) => (
                      <tr key={s.id}>
                        <td>{s.date || "—"}</td>
                        <td>{s.session_type || s.kind || "session"}</td>
                        <td>{s.minutes_actual ?? s.minutes_scheduled ?? "—"}</td>
                        <td>{s.attendance || "—"}</td>
                        <td style={{ fontSize: 11 }}>
                          {(s.notes || "").slice(0, 140) || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="fp-print-section" data-testid="progress-recent-documents">
              <div className="fp-print-section-title">Recent Supporting Documents</div>
              {docs.length === 0 ? (
                <div className="fp-print-empty" style={{ padding: "12px 16px" }}>
                  No supporting documents on file.
                </div>
              ) : (
                <table className="fp-print-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Type</th>
                      <th>Date</th>
                      <th>Freshness</th>
                      <th>Uploaded by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map((d) => (
                      <tr key={d.id}>
                        <td>{d.document_title}</td>
                        <td>{d.document_type}</td>
                        <td>{d.document_date || "—"}</td>
                        <td>{(d.evidence_freshness || "").replace(/_/g, " ")}</td>
                        <td>{d.uploaded_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </PrintAccessGate>
    </PrintShell>
  );
}
